import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router';
import { useResendVerification, useVerifyEmail } from '../../api/queries';
import { Alert, Button, Field, Input, Loading } from '../../components/ui';
import { buttonClass } from '../../components/ui/button-class';
import { errorMessage } from '../../i18n/labels';
import { AuthLayout } from './AuthLayout';

/** "Check your email" with a resend form (FR-ACC-01). */
export function CheckEmail({ email: initial }: { email?: string }) {
  const { t } = useTranslation();
  const resend = useResendVerification();
  const [email, setEmail] = useState(initial ?? '');
  return (
    <div className="space-y-4">
      <p className="text-slate-700 dark:text-slate-300">
        {initial ? t('auth.checkEmailBody', { email: initial }) : t('auth.checkEmailGeneric')}
      </p>
      {resend.isSuccess && <Alert tone="success">{t('auth.verificationResent')}</Alert>}
      {resend.error && <Alert tone="danger">{errorMessage(t, resend.error)}</Alert>}
      <form
        className="space-y-3"
        onSubmit={(e) => {
          e.preventDefault();
          if (email) resend.mutate(email);
        }}
      >
        {!initial && (
          <Field label={t('auth.email')}>
            {(p) => (
              <Input
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                {...p}
              />
            )}
          </Field>
        )}
        <Button type="submit" variant="secondary" className="w-full" loading={resend.isPending}>
          {t('auth.resendVerification')}
        </Button>
      </form>
      <p className="text-center text-sm">
        <Link to="/login" className="link">
          {t('auth.backToSignIn')}
        </Link>
      </p>
    </div>
  );
}

export function VerifyEmailPage() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const token = params.get('token');
  const verify = useVerifyEmail();
  const started = useRef(false);
  const { mutate } = verify;

  useEffect(() => {
    if (token && !started.current) {
      started.current = true;
      mutate(token);
    }
  }, [token, mutate]);

  if (!token) {
    return (
      <AuthLayout title={t('auth.checkEmailTitle')}>
        <CheckEmail email={params.get('email') ?? undefined} />
      </AuthLayout>
    );
  }
  return (
    <AuthLayout title={t('auth.verifyTitle')}>
      {verify.isPending || verify.isIdle ? (
        <Loading label={t('auth.verifying')} />
      ) : verify.isSuccess ? (
        <div className="space-y-4">
          <Alert tone="success" role="status">
            {t('auth.verified')}
          </Alert>
          <Link to="/login" className={buttonClass('primary') + ' w-full'}>
            {t('auth.signIn')}
          </Link>
        </div>
      ) : (
        <div className="space-y-4">
          <Alert tone="danger">{errorMessage(t, verify.error)}</Alert>
          <CheckEmail />
        </div>
      )}
    </AuthLayout>
  );
}
